import { resolveExitTargetPct } from './computeExitQuality.js';
import {
  CLOSE_PURPOSES,
  CLOSE_SHORT_PURPOSES,
  SIBLING_PURPOSES,
  extractLogDay,
  readLogTimestamp,
  readOrderNotional,
} from './manualChartDays.js';

function emptyLegs() {
  return { long: 0, short: 0, total: 0 };
}

function addLeg(bucket, side, amount) {
  bucket[side] += amount;
  bucket.total += amount;
}

/**
 * Entry notional (p × q) of one Spot unit.
 * On target: mark has crossed p × (1 ± margin).
 * One step: not on target yet, and the remaining gap is at most one method margin
 * (long: p ≤ mark < target; short: target < mark ≤ p).
 */
export function classifySpotUnit(side, price, mark, marginFrac) {
  if (!(price > 0) || !(mark > 0) || !(marginFrac > 0)) return null;
  const target = side === 'short'
    ? price * (1 - marginFrac)
    : price * (1 + marginFrac);
  if (side === 'short') {
    if (mark <= target) return 'onTarget';
    if (mark <= price) return 'oneStep';
    return null;
  }
  if (mark >= target) return 'onTarget';
  if (mark >= price) return 'oneStep';
  return null;
}

export function measureSpotAmmo(snapshot, mark, marginFrac) {
  const onTarget = emptyLegs();
  const oneStep = emptyLegs();
  const sides = [
    ['long', snapshot?.spotLongs],
    ['short', snapshot?.spotShorts],
  ];

  sides.forEach(([side, units]) => {
    (units || []).forEach((unit) => {
      const price = Number(unit?.p);
      const qty = Number(unit?.q);
      if (!(price > 0) || !(qty > 0)) return;
      const kind = classifySpotUnit(side, price, mark, marginFrac);
      if (!kind) return;
      const bucket = kind === 'onTarget' ? onTarget : oneStep;
      addLeg(bucket, side, price * qty);
    });
  });

  return { onTarget, oneStep };
}

function snapshotMark(log) {
  const mark = Number(log?.metrics?.currentPrice);
  return mark > 0 ? mark : null;
}

function hasSnapshot(log) {
  return !!log?.metrics?.manualsSnapshot && snapshotMark(log) != null;
}

function pointFrom(snap, field) {
  if (!snap) return null;
  const leg = snap.ammo[field];
  return { long: leg.long, short: leg.short, total: leg.total };
}

function pickPeak(snapshots, field) {
  let best = snapshots[0];
  snapshots.forEach((snap) => {
    if (snap.ammo[field].total > best.ammo[field].total) best = snap;
  });
  return best;
}

/**
 * Retrospective ammunition versus closed USD.
 * Days follow the close chart (sibling purposes) and also appear when a snapshot exists.
 * A day with fills and no snapshot keeps null ammunition. Notional is p × q, not mark × q.
 * Open / remaining are the first and last snapshot of the UTC day.
 * Peak of each series is the max of that series across the day's snapshots.
 */
export function computeAmmoVsRealized(logs, options = {}) {
  const targetMarginPct = resolveExitTargetPct(options.targetMarginPct);
  const marginFrac = targetMarginPct / 100;
  const dayMap = new Map();

  function ensureDay(day) {
    let bucket = dayMap.get(day.dayKey);
    if (!bucket) {
      bucket = {
        dayKey: day.dayKey,
        label: day.label,
        closed: 0,
        closedCount: 0,
        closeLong: 0,
        closeShort: 0,
        snapshots: [],
      };
      dayMap.set(day.dayKey, bucket);
    }
    return bucket;
  }

  (logs || []).forEach((log, index) => {
    if (!log || log.processed === false) return;
    const day = extractLogDay(log);
    if (!day) return;

    const sibling = SIBLING_PURPOSES.has(log.purpose);
    const snapshot = hasSnapshot(log);
    if (!sibling && !snapshot) return;

    const bucket = ensureDay(day);

    if (CLOSE_PURPOSES.has(log.purpose)) {
      const notional = readOrderNotional(log);
      if (Number.isFinite(notional)) {
        bucket.closed += notional;
        bucket.closedCount += 1;
        if (CLOSE_SHORT_PURPOSES.has(log.purpose)) bucket.closeShort += notional;
        else bucket.closeLong += notional;
      }
    }

    if (!snapshot) return;
    const mark = snapshotMark(log);
    bucket.snapshots.push({
      index,
      ts: readLogTimestamp(log) ?? Number.MAX_SAFE_INTEGER,
      ammo: measureSpotAmmo(log.metrics.manualsSnapshot, mark, marginFrac),
    });
  });

  const days = Array.from(dayMap.values())
    .sort((a, b) => a.dayKey.localeCompare(b.dayKey))
    .map((bucket) => {
      const snapshots = bucket.snapshots.sort((a, b) => a.ts - b.ts || a.index - b.index);
      const first = snapshots[0] || null;
      const last = snapshots.length ? snapshots[snapshots.length - 1] : null;
      const onTargetPeak = snapshots.length ? pickPeak(snapshots, 'onTarget') : null;
      const oneStepPeak = snapshots.length ? pickPeak(snapshots, 'oneStep') : null;
      return {
        dayKey: bucket.dayKey,
        label: bucket.label,
        closed: bucket.closedCount ? bucket.closed : null,
        closeLong: bucket.closeLong,
        closeShort: bucket.closeShort,
        hasSnapshot: snapshots.length > 0,
        open: pointFrom(first, 'onTarget'),
        peak: pointFrom(onTargetPeak, 'onTarget'),
        remaining: pointFrom(last, 'onTarget'),
        oneStepOpen: pointFrom(first, 'oneStep'),
        oneStepPeak: pointFrom(oneStepPeak, 'oneStep'),
        oneStepRemaining: pointFrom(last, 'oneStep'),
      };
    });

  return { targetMarginPct, marginFrac, days };
}
